import { SkyButton, SkyText } from "@skypointcloud/skypoint-ui";

/**
 * The editor's save bar (ported from Studio's `SchedulerFooterButtonsV2`).
 *
 * The donor's per-section buttons are gone: it had Next on one section, Save &
 * Next on another and Save & Close on a third, so what the primary button did
 * depended on where you had scrolled. There are three sections behind ONE save
 * here (three cards on one page since Session 10), and the bar says when the schedule
 * was last written so a user who is unsure whether their edit landed can tell.
 */

export interface ScheduleSaveBarProps {
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  /** Disables Save and says why, rather than failing the request to find out. */
  blockedReason?: string;
  /**
   * The user pressed Save while it was blocked. A disabled button gets no click, but it still
   * gets `pointerdown`, which reaches this wrapper; the editor uses it to show each row's own
   * reason (review r3 nit).
   */
  onBlockedAttempt?: () => void;
  lastSavedLabel?: string;
  /**
   * When the schedule runs and its next run, in the chosen zone, e.g. "Runs at 02:00 AM, Eastern
   * (EDT). Next run Tue Sep 30, 2:00 AM EDT"; before a time is chosen, the zone alone.
   */
  summary?: string;
  saveLabel?: string;
}

export function ScheduleSaveBar({
  onSave,
  onCancel,
  isSaving,
  blockedReason,
  onBlockedAttempt,
  lastSavedLabel,
  summary,
  saveLabel = "Save schedule",
}: ScheduleSaveBarProps) {
  return (
    <div
      role="toolbar"
      aria-label="Save schedule"
      data-testid="schedule-save-bar"
      className="sticky bottom-0 z-10 mt-6 flex flex-wrap items-center gap-3 border-t border-border bg-background px-1 py-3"
    >
      <SkyText size={200} color="muted">
        {blockedReason ?? lastSavedLabel ?? ""}
      </SkyText>
      <div className="ml-auto flex items-center gap-2">
        {summary ? (
          <SkyText size={200} weight="medium" testId="schedule-save-summary">
            {summary}
          </SkyText>
        ) : null}
        <SkyButton appearance="secondary" onClick={onCancel} disabled={isSaving}>
          Cancel
        </SkyButton>
        <span
          className="inline-flex"
          onPointerDown={() => {
            if (blockedReason && !isSaving) onBlockedAttempt?.();
          }}
        >
          <SkyButton
            appearance="primary"
            onClick={onSave}
            loading={isSaving}
            disabled={isSaving || Boolean(blockedReason)}
            testId="schedule-save"
          >
            {saveLabel}
          </SkyButton>
        </span>
      </div>
    </div>
  );
}
